import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Draws the strokes of an inline SVG (line-by-line) when it scrolls into view.
 *
 * Wrap an inline `<svg>` in `<svg-draw>`. If instead it wraps an `<img>` pointing
 * to an .svg file (e.g. a metaobject file field), the image is fetched and
 * inlined first so its paths can be animated.
 *
 * On first intersection, every stroked geometry element animates its
 * `stroke-dashoffset` from its full length to 0, "drawing" the illustration.
 * Everything else that isn't a stroke (filled shapes, text, images…) fades in
 * once the lines have been drawn.
 *
 * Attributes:
 *   data-duration — draw duration per element, in ms (default 1200)
 *   data-stagger  — delay between elements, in ms (default 120)
 *   data-replay   — "true" to replay every time it re-enters the viewport
 *
 * @extends {Component}
 */
class SvgDraw extends Component {
  /** @type {IntersectionObserver | null} */
  #io = null;

  connectedCallback() {
    super.connectedCallback();

    const svg = this.querySelector('svg');
    if (svg) {
      this.#setup(svg);
      return;
    }

    // No inline SVG — maybe an <img> to an .svg file. Fetch and inline it.
    const img = this.querySelector('img[data-svg-draw-src], img[src*=".svg"]');
    if (img) this.#inlineFromImage(img);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#io?.disconnect();
  }

  /**
   * Fetch the SVG file behind an <img>, replace the <img> with the inline SVG,
   * then set up the draw. Falls back to leaving the <img> if the fetch fails.
   * @param {HTMLImageElement} img
   */
  #inlineFromImage(img) {
    const src = img.currentSrc || img.src;
    if (!src) return;

    fetch(src)
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error('fetch failed'))))
      .then((markup) => {
        const svg = new DOMParser().parseFromString(markup, 'image/svg+xml').querySelector('svg');
        if (!svg) return;

        // Preserve responsive sizing: drop both the presentational attributes
        // and any inline width/height styles baked into the source file, so the
        // wrapping CSS (width:100%) can take over.
        svg.removeAttribute('width');
        svg.removeAttribute('height');
        svg.style.removeProperty('width');
        svg.style.removeProperty('height');
        img.replaceWith(svg);
        this.#setup(svg);
      })
      .catch(() => {
        // CORS or network error: keep the static image, no animation.
      });
  }

  /**
   * Prepare every stroked shape and reveal them on scroll.
   * @param {SVGSVGElement} svg
   */
  #setup(svg) {
    const duration = parseFloat(this.dataset.duration ?? '1200') || 1200;
    const stagger = parseFloat(this.dataset.stagger ?? '120') || 0;

    /** Geometry with a real stroke → animated by drawing the line. */
    /** @type {SVGGeometryElement[]} */
    const shapes = [];
    /** Everything else that's visible (fills, text, images…) → fades in. */
    /** @type {SVGElement[]} */
    const fades = [];

    for (const el of svg.querySelectorAll('path, line, polyline, polygon, circle, ellipse, rect')) {
      const styles = getComputedStyle(el);
      const strokeWidth = parseFloat(styles.strokeWidth) || 0;
      const hasStroke = styles.stroke !== 'none' && styles.stroke !== '' && strokeWidth > 0;

      let length = 0;
      if (hasStroke) {
        try {
          length = /** @type {SVGGeometryElement} */ (el).getTotalLength();
        } catch {
          length = 0;
        }
      }

      if (length) {
        el.style.strokeDasharray = `${length}`;
        el.style.strokeDashoffset = `${length}`;
        shapes.push(/** @type {SVGGeometryElement} */ (el));
      } else {
        // No drawable stroke (a filled shape, or zero-length geometry): fade it.
        fades.push(/** @type {SVGElement} */ (el));
      }
    }

    // Non-geometry visuals carrying fills/content also fade in.
    for (const el of svg.querySelectorAll('text, image, use')) {
      fades.push(/** @type {SVGElement} */ (el));
    }

    if (!shapes.length && !fades.length) return;

    for (const el of fades) el.style.opacity = '0';

    // Reduced motion: reveal everything instantly.
    if (prefersReducedMotion()) {
      for (const el of shapes) el.style.strokeDashoffset = '0';
      for (const el of fades) el.style.opacity = '1';
      return;
    }

    // Base delay so each item in a list draws after the previous one (stagger).
    const baseDelay = (parseInt(this.dataset.index ?? '0', 10) || 0) * stagger;
    // Replay the animation every time the element re-enters the viewport.
    const replay = this.dataset.replay === 'true';

    /** Animate the strokes, then fade the fills in. */
    const play = () => {
      shapes.forEach((el, index) => {
        el.style.transition = `stroke-dashoffset ${duration}ms cubic-bezier(0.65, 0, 0.35, 1) ${baseDelay + index * stagger}ms`;
        el.style.strokeDashoffset = '0';
      });
      // Fills/text fade in once the lines are drawn, then staggered.
      const fadeStart = baseDelay + shapes.length * stagger;
      fades.forEach((el, index) => {
        el.style.transition = `opacity ${duration}ms ease ${fadeStart + index * stagger}ms`;
        el.style.opacity = '1';
      });
    };

    /** Snap everything back to its hidden state (no transition) so it can replay. */
    const reset = () => {
      for (const el of shapes) {
        el.style.transition = 'none';
        el.style.strokeDashoffset = el.style.strokeDasharray;
      }
      for (const el of fades) {
        el.style.transition = 'none';
        el.style.opacity = '0';
      }
    };

    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            play();
            this.classList.add('is-drawn');
            if (!replay) {
              this.#io?.disconnect();
              this.#io = null;
            }
          } else if (replay) {
            reset();
            this.classList.remove('is-drawn');
          }
        }
      },
      { threshold: 0.25 }
    );

    this.#io.observe(this);
  }
}

if (!customElements.get('svg-draw')) {
  customElements.define('svg-draw', SvgDraw);
}
